<script setup lang="ts">
/**
 * "Your organization needs an IT admin to approve AutomaDoc": what a person sees when
 * Microsoft refused their sign-in or connection because their tenant lets only admins
 * consent (MS_CONSENT_REQUIRED, AADSTS65001 / 90094 / 90095). Common while the app's
 * publisher is unverified, which turns most user consent into an admin request.
 *
 * The link to forward is the portal's IT admin page (views/MicrosoftAdmin.vue), with the
 * refusal's directory: it lists every permission and approves them in one click, which a
 * bare Microsoft consent URL does not explain. "I am the admin" opens the API's own link
 * (exactly the scopes that were refused, with a state the API reads back).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ShieldAlert } from 'lucide-vue-next'
import { microsoftAdminPageUrl } from '@lark-automadoc/shared-types'
import CopyField from '@/components/CopyField.vue'

const props = defineProps<{
  /** The API's admin-consent link, when the refusal carried one. */
  consentUrl: string | null
  /** What the person does once their admin has approved. */
  after: 'signin' | 'connect'
}>()

const { t } = useI18n()

const pageUrl = computed(() => microsoftAdminPageUrl(window.location.origin, props.consentUrl))
</script>

<template>
  <div role="status" class="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-100">
    <p class="flex items-start gap-2 font-medium">
      <ShieldAlert :size="16" class="mt-0.5 shrink-0" aria-hidden="true" />
      {{ t('adminApprovalTitle') }}
    </p>
    <p class="mt-1">{{ t('adminApprovalBody') }}</p>
    <div class="mt-3">
      <CopyField :value="pageUrl" :label="t('adminApprovalLink')" />
    </div>
    <p class="mt-3 text-xs">
      {{ after === 'signin' ? t('adminApprovalAfterSignIn') : t('adminApprovalAfterConnect') }}
      <a :href="consentUrl ?? pageUrl" class="font-medium underline">{{ t('connectionsConsentAsAdmin') }}</a>
    </p>
  </div>
</template>
